import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { PasswordInput } from '@/components/ui/input-password';
import { Label } from '@/components/ui/label';
import { useAuthContext } from '@/context/auth-provider';
import { toast } from '@/hooks/use-toast';
import {
    destroyBackupCodesMutationFn,
    generateBackupCodesMutationFn,
} from '@/lib/api';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { AlertCircle, Check, Copy, DownloadIcon, Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import FormattedMessage from '../formatted-message';

export interface BackupCode {
    id: number;
    code: string;
    used: boolean;
}
function BackupCodes() {
    const [isBackupCodesOpen, setIsBackupCodesOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [isDestroyingBackupCodes, setIsDestroyingBackupCodes] =
        useState(false);
    const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
    const [isCopiedAll, setIsCopiedAll] = useState(false);
    const { user, emailFA, totpFA, hasBackupCodes, refetch } = useAuthContext();
    const shortWebsiteName = 'ASM';
    const longWebsiteName = 'Advanced Sessions Management';

    const { mutate, isPending } = useMutation({
        mutationFn: isDestroyingBackupCodes
            ? destroyBackupCodesMutationFn
            : generateBackupCodesMutationFn,
    });

    const handlePasswordSubmit = async (
        e: React.FormEvent<HTMLFormElement>,
    ) => {
        e.preventDefault();
        setError('');
        try {
            mutate(
                { userId: user.id, password },
                {
                    onSuccess: (response) => {
                        if (isDestroyingBackupCodes) {
                            toast({
                                title: 'Done',
                                description: response.data.message,
                            });
                            setIsDestroyingBackupCodes(false);
                            handleClose();
                        } else {
                            setBackupCodes(response.data.backupCodes);
                            setStep(2);
                        }
                        refetch();
                    },
                    onError: (error) => {
                        setError(
                            error.message || 'Failed to generate backup codes.',
                        );
                    },
                },
            );
        } catch (error) {
            if (error instanceof Error) {
                setError((error.message || 'Internal Server Error') as string);
            }
        }
    };

    const handleCopyCode = (code: string, index: number) => {
        navigator.clipboard.writeText(code);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 3000);

        toast({
            title: 'Success',
            description: 'The selected code is copied.',
        });
    };

    const handleCopyAll = () => {
        const allCodes = backupCodes.join('\n');
        navigator.clipboard.writeText(allCodes);
        setIsCopiedAll(true);
        setTimeout(() => setIsCopiedAll(false), 2500);
        toast({
            title: 'Copied',
            description: 'All backup codes are copied.',
        });
    };

    const handleClose = useCallback(() => {
        setStep(0);
        setPassword('');
        setError('');
        setBackupCodes([]);
        setIsBackupCodesOpen(false);
    }, []);

    const generateBackupCodesContent = () => {
        const content = `
===================================================
          ${longWebsiteName}
           BACKUP CODES - KEEP SECURE
===================================================

Date Generated: ${format(new Date(), 'EEEE, d MMM, yyyy, p z')}
Generated For: ${user.email} Account

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
                YOUR BACKUP CODES
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

${backupCodes
    .slice(0, 5)
    .map(
        (code, index) =>
            `${String(index + 1).padStart(2, '0')}. ${code}              ${String(index + 1 + 5).padStart(2, '0')}. ${backupCodes[index + 5]}`,
    )
    .join('\n')}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
             IMPORTANT INSTRUCTIONS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. Store these codes in a secure location.
2. Each code can only be used once.
3. Keep these codes separate from your device.
4. Never share these codes with anyone.
5. Treat these codes like passwords.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${shortWebsiteName} Security Team
https://${(import.meta.env.VITE_APP_URL || shortWebsiteName + '.com').toLowerCase()}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Note: If you lose access to your account,
use these codes to verify your identity.
`;
        return content;
    };

    const downloadBackupCodes = () => {
        const content = generateBackupCodesContent();

        // Create a blob with the content
        const blob = new Blob([content], { type: 'text/plain' });

        // Create a download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        // Set the file name with date
        const date = format(new Date(), 'd-MMM-yyyy');
        link.href = url;
        link.download = `${shortWebsiteName}_backup_codes_${user.email.slice(0, user.email.indexOf('@'))}_${date}.txt`;

        // Trigger download
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        const handleEscapePress = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        };

        window.addEventListener('keydown', handleEscapePress);
        return () => window.removeEventListener('keydown', handleEscapePress);
    }, [handleClose]);

    return (
        <div className='flex flex-col gap-5'>
            <div className='relative sm:pl-6 transition duration-200 ease-in-out'>
                <div className='bg-white dark:bg-background  absolute -left-[9.5px] top-[2.1rem] z-10 block size-6 sm:size-5 rounded-full'>
                    <div className='ml-1 mt-1 size-4 sm:size-3 rounded-full border-2 transition duration-200 ease-in-out border-primary'></div>
                </div>
                <div className='via-root to-root rounded-xl bg-gradient-to-r p-0.5'>
                    <div className='rounded-[10px] bg-success/15 dark:bg-success/25 p-6'>
                        <div className='flex items-center gap-3'>
                            <p className='sm:text-lg tracking-[-0.16px] text-slate-12 font-bold mb-1'>
                                Backup Codes
                            </p>
                            {!user.security.mfa.some((m) => m.isEnabled) && (
                                <Badge variant={'destructive'}>
                                    MFA is disabled
                                </Badge>
                            )}
                        </div>
                        <p className='mb-6 text-sm text-[#0007149f] dark:text-gray-100 font-normal'>
                            Backup codes are one-time-use, numeric, or
                            alphanumeric, codes used as a secure, offline,
                            secondary authentication method when you lose access
                            to your primary 2FA device (phone/app) or
                            email.{' '}
                        </p>
                        {!user.security.mfa.some((m) => m.isEnabled) && (
                            <p className='max-w-max text-[0.8rem] text-warning bg-warning/15 my-4 p-2 rounded-md flex flex-col md:flex-row gap-2 text-center md:text-start items-center'>
                                <AlertCircle size={20} />
                                <span className='font-bold'>Warning:</span>{' '}
                                Backup codes can&apos;t be
                                generated/destroyed/used if all MFA methods are
                                disabled.
                                <br />
                                Activate at least one method to use backup
                                codes.
                            </p>
                        )}
                        <div className='w-full flex justify-start gap-4'>
                            <Dialog
                                modal
                                open={isBackupCodesOpen}
                                onOpenChange={setIsBackupCodesOpen}>
                                <DialogTrigger asChild>
                                    <Button
                                        disabled={
                                            !emailFA.isEnabled &&
                                            !totpFA.isEnabled
                                        }
                                        onClick={() => {
                                            setStep(1);
                                            setPassword('');
                                            setError('');
                                        }}
                                        className='h-[2.5rem] w-[7rem] py-1'
                                        variant={'outline'}>
                                        {hasBackupCodes
                                            ? 'Regenerate'
                                            : 'Generate'}
                                    </Button>
                                </DialogTrigger>
                                <DialogContent
                                    className={`!gap-0 ${step === 1 ? 'max-w-xl' : 'max-w-6xl'}`}
                                    aria-describedby='Backup Codes'>
                                    <DialogHeader>
                                        <DialogTitle className='text-2xl text-slate-12 font-semibold'>
                                            {step === 1
                                                ? 'Enter Password'
                                                : 'Your Backup Codes'}
                                        </DialogTitle>
                                    </DialogHeader>
                                    <DialogDescription className='mb-6'>
                                        {step === 1
                                            ? `Please enter your password to ${isDestroyingBackupCodes ? 'destroy' : hasBackupCodes ? 'Re-Generate and view' : 'generate and view'} your new backup-codes.`
                                            : 'Save these backup codes in a secure place. Each code can only be used once.'}
                                    </DialogDescription>
                                    {step === 1 ? (
                                        <form
                                            onSubmit={handlePasswordSubmit}
                                            className='space-y-4'>
                                            <div className='space-y-2'>
                                                <Label htmlFor='password'>
                                                    Password
                                                </Label>
                                                <PasswordInput
                                                    id='password'
                                                    placeholder='Enter your password'
                                                    value={password}
                                                    onChange={(e) =>
                                                        setPassword(
                                                            e.target.value,
                                                        )
                                                    }
                                                    disabled={isPending}
                                                    autoComplete='current-password'
                                                />
                                            </div>

                                            {error && (
                                                <Alert
                                                    variant='destructive'
                                                    className='h-16 py-0 flex items-center [&>svg]:sticky'>
                                                    <AlertCircle className='size-7' />
                                                    <AlertDescription className='!translate-y-0'>
                                                        <FormattedMessage
                                                            message={error}
                                                        />
                                                    </AlertDescription>
                                                </Alert>
                                            )}

                                            <div className='flex justify-end space-x-2'>
                                                <Button
                                                    type='button'
                                                    variant='outline'
                                                    onClick={handleClose}
                                                    disabled={isPending}>
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type='submit'
                                                    disabled={isPending}>
                                                    {isPending && (
                                                        <Loader2 className='mr-2 size-4 animate-spin' />
                                                    )}
                                                    {isPending
                                                        ? 'Verifying...'
                                                        : 'Continue'}
                                                </Button>
                                            </div>
                                        </form>
                                    ) : (
                                        <div className='space-y-4'>
                                            <div className='grid grid-cols-2 lg:grid-cols-5 gap-2 max-h-96 overflow-y-auto p-1'>
                                                {backupCodes.map(
                                                    (item, index) => (
                                                        <div
                                                            key={index}
                                                            className='flex items-center justify-between p-3 h-16 bg-secondary/20 rounded-lg border group hover:border-primary transition-colors'>
                                                            <div className='flex items-center space-x-3'>
                                                                <code className='text-[0.7rem] md:text-[0.8rem] font-mono'>
                                                                    {item}
                                                                </code>
                                                            </div>
                                                            <Button
                                                                variant='ghost'
                                                                size='sm'
                                                                onClick={() =>
                                                                    handleCopyCode(
                                                                        item,
                                                                        index,
                                                                    )
                                                                }
                                                                className={`${copiedIndex !== index ? 'hidden' : ''} group-hover:block transition-all`}>
                                                                {copiedIndex ===
                                                                index ? (
                                                                    <Check className='size-4 text-green-500' />
                                                                ) : (
                                                                    <Copy className='size-4' />
                                                                )}
                                                            </Button>
                                                        </div>
                                                    ),
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className='flex justify-between items-center pt-4 border-t'>
                                                <div className='text-[0.8rem] md:text-sm text-muted-foreground'>
                                                    {backupCodes.length} codes
                                                </div>
                                                <div className='flex space-x-2'>
                                                    <Button
                                                        onClick={
                                                            downloadBackupCodes
                                                        }
                                                        variant={'secondary'}>
                                                        <DownloadIcon className='size5' />
                                                        Download
                                                    </Button>
                                                    <Button
                                                        variant='outline'
                                                        onClick={handleCopyAll}
                                                        className='!min-w-32 p-0'>
                                                        {isCopiedAll ? (
                                                            <Check className='mr-2 size-4 text-green-500' />
                                                        ) : (
                                                            <Copy className='mr-2 size-4' />
                                                        )}
                                                        Copy All
                                                    </Button>
                                                    <Button
                                                        onClick={handleClose}>
                                                        Done
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Warning Alert */}
                                            <Alert className=''>
                                                <AlertDescription className='flex flex-col md:flex-row justify-start items-center gap-2 text-[0.8rem]'>
                                                    <AlertCircle className='size-6 inline' />
                                                    <span className='font-bold md:text-[1rem]'>
                                                        Tip:{' '}
                                                    </span>
                                                    Store these codes now
                                                    securely. If you lose access
                                                    to your authenticator app
                                                    or/and email, you&apos;ll
                                                    need these codes to recover
                                                    your account.
                                                </AlertDescription>
                                            </Alert>
                                        </div>
                                    )}
                                </DialogContent>
                            </Dialog>
                            <Button
                                disabled={
                                    !hasBackupCodes ||
                                    (!emailFA.isEnabled && !totpFA.isEnabled)
                                }
                                onClick={() => {
                                    setStep(1);
                                    setIsBackupCodesOpen(true);
                                    setIsDestroyingBackupCodes(true);
                                    setPassword('');
                                    setError('');
                                }}
                                className='h-[2.5rem] w-[7rem] py-1'
                                title='Destroy all backup codes'
                                variant={'destructive'}>
                                Destroy
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BackupCodes;
